import React, { useCallback, useMemo, useState } from "react";
import {
  Page,
  Card,
  Text,
  Button,
  ButtonGroup,
  Badge,
  Avatar,
  Divider,
  InlineStack,
  BlockStack,
  Box,
  Tooltip,
  Icon,
  Tabs,
  Select,
  Modal,
  TextField,
  ChoiceList,
  DropZone,
  Thumbnail,
  Banner,
  Link,
} from "@shopify/polaris";
import {
  CalendarIcon,
  CheckCircleIcon,
  MobileIcon,
  CalendarTimeIcon,
  PlusCircleIcon,
  ImageIcon,
  DeleteIcon,
  ViewIcon,
  StoreIcon,
} from "@shopify/polaris-icons";

// --- Assets (keep your existing image imports the same names/paths) ---
// import avatar1 from "@/assets/avatar-1.jpg";
// import avatar2 from "@/assets/avatar-2.jpg";
// import avatar3 from "@/assets/avatar-3.jpg";
// import avatar4 from "@/assets/avatar-4.jpg";
// import avatar5 from "@/assets/avatar-5.jpg";
// import screenshot1 from "@/assets/screenshot-1.jpg";
// import screenshot2 from "@/assets/screenshot-2.jpg";
// import screenshot3 from "@/assets/screenshot-3.jpg";
// Replace your local imports with these URLs:

const avatar1 = "https://randomuser.me/api/portraits/women/65.jpg";
const avatar2 = "https://randomuser.me/api/portraits/men/32.jpg";
const avatar3 = "https://randomuser.me/api/portraits/women/44.jpg";
const avatar4 = "https://randomuser.me/api/portraits/men/76.jpg";
const avatar5 = "https://randomuser.me/api/portraits/women/12.jpg";

const screenshot1 =
  "https://images.unsplash.com/photo-1557682260-96773eb01377?auto=format&fit=crop&w=800&q=80";
const screenshot2 =
  "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=800&q=80";
const screenshot3 =
  "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=800&q=80";

// --- Mock data (same content as your original) ---
const mockTasks = [
  {
    id: "1",
    title: "Design new login screen",
    description: "Create modern authentication UI",
    status: "backlog",
    assignee: { name: "Sarah Chen", avatar: avatar1 },
    reporter: { name: "Marcus Williams", avatar: avatar2 },
    deadline: "2024-03-20",
    screenshots: [screenshot1, screenshot2],
    attachments: 3,
    project: "mobile",
    country: "lebanon",
    group: "Authentication",
  },
  {
    id: "2",
    title: "Implement user profile API",
    description: "Backend endpoints for user data",
    status: "backlog",
    assignee: { name: "Elena Rodriguez", avatar: avatar3 },
    reporter: { name: "David Park", avatar: avatar4 },
    deadline: "2024-03-22",
    attachments: 1,
    project: "mobile",
    country: "lebanon",
    group: "Authentication",
  },
  {
    id: "3",
    title: "Update homepage layout",
    description: "Responsive design improvements",
    status: "backlog",
    assignee: { name: "Jessica Martinez", avatar: avatar5 },
    reporter: { name: "Sarah Chen", avatar: avatar1 },
    deadline: "2024-03-18",
    screenshots: [screenshot3],
    attachments: 2,
    project: "website",
    country: "egypt",
    group: "UI/UX",
  },
  {
    id: "4",
    title: "Add payment gateway",
    description: "Integrate Stripe payments",
    status: "todo",
    assignee: { name: "Marcus Williams", avatar: avatar2 },
    reporter: { name: "Elena Rodriguez", avatar: avatar3 },
    deadline: "2024-03-25",
    project: "website",
    country: "egypt",
    group: "Features",
  },
  {
    id: "5",
    title: "Refactor authentication flow",
    description: "Improve security and user experience",
    status: "todo",
    assignee: { name: "David Park", avatar: avatar4 },
    reporter: { name: "Jessica Martinez", avatar: avatar5 },
    deadline: "2024-03-21",
    project: "mobile",
    country: "lebanon",
    group: "Features",
  },
  {
    id: "6",
    title: "Create user dashboard",
    description: "Build analytics dashboard for users",
    status: "todo",
    assignee: { name: "Sarah Chen", avatar: avatar1 },
    reporter: { name: "Marcus Williams", avatar: avatar2 },
    deadline: "2024-03-23",
    screenshots: [screenshot1],
    project: "website",
    country: "iraq",
    group: "UI/UX",
  },
  {
    id: "7",
    title: "Fix mobile navigation",
    description: "Resolve hamburger menu issues",
    status: "selected",
    assignee: { name: "Elena Rodriguez", avatar: avatar3 },
    reporter: { name: "David Park", avatar: avatar4 },
    deadline: "2024-03-16",
    screenshots: [screenshot2],
    project: "mobile",
    country: "iraq",
    group: "Bug Fixes",
  },
  {
    id: "8",
    title: "Implement dark mode",
    description: "Add theme switching functionality",
    status: "in-progress",
    assignee: { name: "Jessica Martinez", avatar: avatar5 },
    reporter: { name: "Sarah Chen", avatar: avatar1 },
    deadline: "2024-03-17",
    project: "website",
    country: "egypt",
    group: "Features",
  },
  {
    id: "9",
    title: "Optimize image loading",
    description: "Implement lazy loading for images",
    status: "in-progress",
    assignee: { name: "Marcus Williams", avatar: avatar2 },
    reporter: { name: "Elena Rodriguez", avatar: avatar3 },
    deadline: "2024-03-18",
    screenshots: [screenshot3, screenshot1],
    project: "mobile",
    country: "lebanon",
    group: "Performance",
  },
  {
    id: "10",
    title: "Update privacy policy",
    description: "Comply with GDPR regulations",
    status: "in-progress",
    assignee: { name: "David Park", avatar: avatar4 },
    reporter: { name: "Jessica Martinez", avatar: avatar5 },
    deadline: "2024-03-19",
    project: "website",
    country: "iraq",
    group: "Documentation",
  },
  {
    id: "11",
    title: "Optimize database queries",
    description: "Improve performance",
    status: "qa",
    assignee: { name: "Sarah Chen", avatar: avatar1 },
    reporter: { name: "Marcus Williams", avatar: avatar2 },
    deadline: "2024-03-15",
    project: "website",
    country: "iraq",
    group: "Performance",
  },
  {
    id: "12",
    title: "Test payment integration",
    description: "Verify payment flows work correctly",
    status: "qa",
    assignee: { name: "Elena Rodriguez", avatar: avatar3 },
    reporter: { name: "David Park", avatar: avatar4 },
    deadline: "2024-03-16",
    screenshots: [screenshot2],
    project: "website",
    country: "egypt",
    group: "Testing",
  },
  {
    id: "13",
    title: "Setup CI/CD pipeline",
    description: "Automated deployment",
    status: "done",
    assignee: { name: "Jessica Martinez", avatar: avatar5 },
    reporter: { name: "Sarah Chen", avatar: avatar1 },
    deadline: "2024-03-10",
    project: "mobile",
    country: "lebanon",
    group: "DevOps",
  },
  {
    id: "14",
    title: "Write API documentation",
    description: "Document all endpoints",
    status: "done",
    assignee: { name: "Marcus Williams", avatar: avatar2 },
    reporter: { name: "Elena Rodriguez", avatar: avatar3 },
    deadline: "2024-03-11",
    screenshots: [screenshot1, screenshot3],
    project: "website",
    country: "egypt",
    group: "Documentation",
  },
  {
    id: "15",
    title: "Migrate to new server",
    description: "Move infrastructure to new hosting",
    status: "done",
    assignee: { name: "David Park", avatar: avatar4 },
    reporter: { name: "Jessica Martinez", avatar: avatar5 },
    deadline: "2024-03-12",
    project: "mobile",
    country: "iraq",
    group: "DevOps",
  },
  {
    id: "16",
    title: "Implement search functionality",
    description: "Add search bar with filters",
    status: "backlog",
    assignee: { name: "Sarah Chen", avatar: avatar1 },
    reporter: { name: "Marcus Williams", avatar: avatar2 },
    deadline: "2024-03-24",
    screenshots: [screenshot1],
    project: "website",
    country: "lebanon",
    group: "Features",
  },
  {
    id: "17",
    title: "Create mobile onboarding",
    description: "Design welcome screens for new users",
    status: "backlog",
    assignee: { name: "Elena Rodriguez", avatar: avatar3 },
    reporter: { name: "David Park", avatar: avatar4 },
    deadline: "2024-03-26",
    project: "mobile",
    country: "egypt",
    group: "UI/UX",
  },
  {
    id: "18",
    title: "Add push notifications",
    description: "Implement notification system",
    status: "backlog",
    assignee: { name: "Jessica Martinez", avatar: avatar5 },
    reporter: { name: "Sarah Chen", avatar: avatar1 },
    deadline: "2024-03-27",
    screenshots: [screenshot2, screenshot3],
    project: "mobile",
    country: "lebanon",
    group: "Features",
  },
  {
    id: "19",
    title: "Optimize CSS bundle",
    description: "Reduce CSS file size",
    status: "backlog",
    assignee: { name: "Marcus Williams", avatar: avatar2 },
    reporter: { name: "Elena Rodriguez", avatar: avatar3 },
    deadline: "2024-03-28",
    project: "website",
    country: "iraq",
    group: "Performance",
  },
  {
    id: "20",
    title: "Create admin dashboard",
    description: "Build admin panel for management",
    status: "backlog",
    assignee: { name: "David Park", avatar: avatar4 },
    reporter: { name: "Jessica Martinez", avatar: avatar5 },
    deadline: "2024-03-29",
    screenshots: [screenshot1, screenshot2],
    project: "website",
    country: "egypt",
    group: "Features",
  },
  {
    id: "21",
    title: "Fix memory leaks",
    description: "Resolve memory issues in app",
    status: "backlog",
    assignee: { name: "Sarah Chen", avatar: avatar1 },
    reporter: { name: "Marcus Williams", avatar: avatar2 },
    deadline: "2024-03-30",
    project: "mobile",
    country: "lebanon",
    group: "Bug Fixes",
  },
  {
    id: "22",
    title: "Add multi-language support",
    description: "Implement i18n for multiple languages",
    status: "backlog",
    assignee: { name: "Elena Rodriguez", avatar: avatar3 },
    reporter: { name: "David Park", avatar: avatar4 },
    deadline: "2024-03-31",
    project: "website",
    country: "iraq",
    group: "Features",
  },
  {
    id: "23",
    title: "Create email templates",
    description: "Design transactional email templates",
    status: "backlog",
    assignee: { name: "Jessica Martinez", avatar: avatar5 },
    reporter: { name: "Sarah Chen", avatar: avatar1 },
    deadline: "2024-04-01",
    screenshots: [screenshot3],
    project: "website",
    country: "egypt",
    group: "UI/UX",
  },
  {
    id: "24",
    title: "Implement OAuth login",
    description: "Add social login options",
    status: "backlog",
    assignee: { name: "Marcus Williams", avatar: avatar2 },
    reporter: { name: "Elena Rodriguez", avatar: avatar3 },
    deadline: "2024-04-02",
    project: "mobile",
    country: "lebanon",
    group: "Authentication",
  },
  {
    id: "25",
    title: "Add data export feature",
    description: "Allow users to export their data",
    status: "backlog",
    assignee: { name: "David Park", avatar: avatar4 },
    reporter: { name: "Jessica Martinez", avatar: avatar5 },
    deadline: "2024-04-03",
    project: "website",
    country: "iraq",
    group: "Features",
  },
  {
    id: "26",
    title: "Optimize API responses",
    description: "Improve API performance",
    status: "backlog",
    assignee: { name: "Sarah Chen", avatar: avatar1 },
    reporter: { name: "Marcus Williams", avatar: avatar2 },
    deadline: "2024-04-04",
    screenshots: [screenshot1, screenshot2],
    project: "mobile",
    country: "egypt",
    group: "Performance",
  },
  {
    id: "27",
    title: "Create user tutorial",
    description: "Build interactive tutorial",
    status: "backlog",
    assignee: { name: "Elena Rodriguez", avatar: avatar3 },
    reporter: { name: "David Park", avatar: avatar4 },
    deadline: "2024-04-05",
    project: "website",
    country: "lebanon",
    group: "Documentation",
  },
  {
    id: "28",
    title: "Add offline mode",
    description: "Enable app to work offline",
    status: "backlog",
    assignee: { name: "Jessica Martinez", avatar: avatar5 },
    reporter: { name: "Sarah Chen", avatar: avatar1 },
    deadline: "2024-04-06",
    screenshots: [screenshot3],
    project: "mobile",
    country: "iraq",
    group: "Features",
  },
  {
    id: "29",
    title: "Implement rate limiting",
    description: "Add API rate limiting",
    status: "backlog",
    assignee: { name: "Marcus Williams", avatar: avatar2 },
    reporter: { name: "Elena Rodriguez", avatar: avatar3 },
    deadline: "2024-04-07",
    project: "website",
    country: "egypt",
    group: "Security",
  },
  {
    id: "30",
    title: "Create analytics dashboard",
    description: "Build reporting and analytics",
    status: "backlog",
    assignee: { name: "David Park", avatar: avatar4 },
    reporter: { name: "Jessica Martinez", avatar: avatar5 },
    deadline: "2024-04-08",
    screenshots: [screenshot1, screenshot3],
    project: "website",
    country: "lebanon",
    group: "Features",
  },
  {
    id: "31",
    title: "Add video upload support",
    description: "Enable video file uploads",
    status: "backlog",
    assignee: { name: "Sarah Chen", avatar: avatar1 },
    reporter: { name: "Marcus Williams", avatar: avatar2 },
    deadline: "2024-04-09",
    project: "mobile",
    country: "iraq",
    group: "Features",
  },
  {
    id: "32",
    title: "Optimize mobile performance",
    description: "Improve app loading speed",
    status: "backlog",
    assignee: { name: "Elena Rodriguez", avatar: avatar3 },
    reporter: { name: "David Park", avatar: avatar4 },
    deadline: "2024-04-10",
    screenshots: [screenshot2],
    project: "mobile",
    country: "egypt",
    group: "Performance",
  },
  {
    id: "33",
    title: "Create feedback form",
    description: "Build user feedback collection",
    status: "backlog",
    assignee: { name: "Jessica Martinez", avatar: avatar5 },
    reporter: { name: "Sarah Chen", avatar: avatar1 },
    deadline: "2024-04-11",
    project: "website",
    country: "lebanon",
    group: "Features",
  },
  {
    id: "34",
    title: "Implement websockets",
    description: "Add real-time communication",
    status: "backlog",
    assignee: { name: "Marcus Williams", avatar: avatar2 },
    reporter: { name: "Elena Rodriguez", avatar: avatar3 },
    deadline: "2024-04-12",
    screenshots: [screenshot1, screenshot2, screenshot3],
    project: "mobile",
    country: "iraq",
    group: "Features",
  },
  {
    id: "35",
    title: "Add accessibility features",
    description: "Improve WCAG compliance",
    status: "backlog",
    assignee: { name: "David Park", avatar: avatar4 },
    reporter: { name: "Jessica Martinez", avatar: avatar5 },
    deadline: "2024-04-13",
    project: "website",
    country: "egypt",
    group: "Accessibility",
  },
];

const statusOrder = [
  { key: "backlog", label: "Backlog" },
  { key: "todo", label: "To Do" },
  { key: "selected", label: "Selected" },
  { key: "in-progress", label: "In Progress" },
  { key: "qa", label: "QA" },
  { key: "done", label: "Done" },
];

const countryFlag = {
  lebanon: "🇱🇧",
  egypt: "🇪🇬",
  iraq: "🇮🇶",
};

function TaskManager() {
  const [selectedProject, setSelectedProject] = useState("all"); // "all" | "mobile" | "website"
  const [selectedCountries, setSelectedCountries] = useState([
    "lebanon",
    "egypt",
    "iraq",
  ]);
  const [viewMode, setViewMode] = useState("list"); // "list" | "kanban" | "timeline"
  const [kanbanDisplayMode, setKanbanDisplayMode] = useState("compact"); // "super-compact" | "compact" | "relaxed"

  const [tasks, setTasks] = useState(
    mockTasks.map((t, i) => ({ ...t, order: i }))
  );

  const [selectedTab, setSelectedTab] = useState(0);
  const [detailsModal, setDetailsModal] = useState({ open: false, task: null });
  const [isEditingDesc, setIsEditingDesc] = useState(false);
  const [editedDesc, setEditedDesc] = useState("");

  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkInput, setBulkInput] = useState("");
  const [parsedBulk, setParsedBulk] = useState([]);

  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [imageTasks, setImageTasks] = useState([]);
  const [aiDetectionEnabled, setAiDetectionEnabled] = useState(true);

  // Random but stable maps per task
  const taskDaysMap = useMemo(() => {
    const map = new Map();
    tasks.forEach((t) => {
      if (!map.has(t.id)) {
        map.set(t.id, Math.floor(Math.random() * 131) - 30);
      }
    });
    return map;
  }, [tasks.map((t) => t.id).join(",")]);

  const highPriorityMap = useMemo(() => {
    const map = new Map();
    tasks.forEach((t) => {
      if (!map.has(t.id)) {
        map.set(t.id, Math.random() < 0.07); // ~7%
      }
    });
    return map;
  }, [tasks.map((t) => t.id).join(",")]);

  const getDaysLeft = (taskId) => taskDaysMap.get(taskId) ?? 0;
  const isHighPriority = (taskId) => highPriorityMap.get(taskId) ?? false;

  const toggleCountry = (country) => {
    setSelectedCountries((prev) =>
      prev.includes(country)
        ? prev.filter((c) => c !== country)
        : [...prev, country]
    );
  };

  const filterByTopBar = useCallback(
    (task) => {
      const matchesProject =
        selectedProject === "all" || task.project === selectedProject;
      const matchesCountry = selectedCountries.includes(task.country);
      return matchesProject && matchesCountry;
    },
    [selectedProject, selectedCountries]
  );

  const filteredTasks = tasks.filter(filterByTopBar);

  const counts = useMemo(() => {
    return {
      backlog: filteredTasks.filter((t) => t.status === "backlog").length,
      todo: filteredTasks.filter((t) => t.status === "todo").length,
      selected: filteredTasks.filter((t) => t.status === "selected").length,
      inProgress: filteredTasks.filter((t) => t.status === "in-progress")
        .length,
      qa: filteredTasks.filter((t) => t.status === "qa").length,
      done: filteredTasks.filter((t) => t.status === "done").length,
    };
  }, [filteredTasks]);

  const tabs = [
    {
      id: "backlog",
      content: `Backlog (${counts.backlog})`,
      panelID: "backlog-panel",
    },
    { id: "todo", content: `To Do (${counts.todo})`, panelID: "todo-panel" },
    {
      id: "selected",
      content: `Selected (${counts.selected})`,
      panelID: "selected-panel",
    },
    {
      id: "in-progress",
      content: `In Progress (${counts.inProgress})`,
      panelID: "in-progress-panel",
    },
    { id: "qa", content: `QA (${counts.qa})`, panelID: "qa-panel" },
    { id: "done", content: `Done (${counts.done})`, panelID: "done-panel" },
  ];

  const selectedStatus = tabs[selectedTab].id;

  const getGroupedTasksByStatus = (status) => {
    const list = filteredTasks
      .filter((t) => t.status === status)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const grouped = {};
    list.forEach((t) => {
      grouped[t.group] = grouped[t.group] || [];
      grouped[t.group].push(t);
    });
    return grouped;
  };

  const onTaskClick = (task) => {
    setDetailsModal({ open: true, task });
    setEditedDesc(task.description || "");
    setIsEditingDesc(false);
  };

  const moveTask = (taskId, newStatus) => {
    setTasks((prev) =>
      prev.map((t) => (t.id === taskId ? { ...t, status: newStatus } : t))
    );
  };

  // Basic reordering inside the same column (no HTML5 DnD to keep it Polaris-native/simple)
  const moveUp = (task) => {
    const inColumn = tasks
      .filter((t) => t.status === task.status)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const idx = inColumn.findIndex((t) => t.id === task.id);
    if (idx <= 0) return;
    const before = inColumn[idx - 1];
    setTasks((prev) =>
      prev.map((t) => {
        if (t.id === task.id) return { ...t, order: before.order ?? 0 };
        if (t.id === before.id) return { ...t, order: task.order ?? 0 };
        return t;
      })
    );
  };
  const moveDown = (task) => {
    const inColumn = tasks
      .filter((t) => t.status === task.status)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const idx = inColumn.findIndex((t) => t.id === task.id);
    if (idx === -1 || idx === inColumn.length - 1) return;
    const after = inColumn[idx + 1];
    setTasks((prev) =>
      prev.map((t) => {
        if (t.id === task.id) return { ...t, order: after.order ?? 0 };
        if (t.id === after.id) return { ...t, order: task.order ?? 0 };
        return t;
      })
    );
  };

  const getRelativeDeadline = (deadline) => {
    try {
      const deadlineDate = new Date(deadline);
      const now = new Date();
      const diffTime = deadlineDate.getTime() - now.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      if (diffDays === 1) return "1 Left";
      if (diffDays > 1) return `${diffDays} Left`;
      if (diffDays === 0) return "Today";
      return `${Math.abs(diffDays)} Overdue`;
    } catch {
      return deadline;
    }
  };

  // Bulk add (text)
  const parseBulkNow = () => {
    const lines = (bulkInput || "")
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean);
    setParsedBulk(lines.map((title) => ({ title, assignee: "Sarah Chen" })));
  };
  const confirmBulkAdd = () => {
    const baseOrder = tasks.length;
    const toAdd = parsedBulk.map((p, idx) => ({
      id: String(Date.now() + idx),
      title: p.title,
      description: "",
      status: "backlog",
      assignee: { name: p.assignee, avatar: avatar1 },
      reporter: { name: "Auto Import", avatar: avatar2 },
      deadline: new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10),
      project: "website",
      country: "lebanon",
      group: "Imported",
      order: baseOrder + idx,
    }));
    setTasks((prev) => [...prev, ...toAdd]);
    setParsedBulk([]);
    setBulkInput("");
    setBulkModalOpen(false);
  };

  // Images -> create tasks
  const handleDropZoneDrop = (_dropFiles, acceptedFiles, _rejectedFiles) => {
    setUploadedFiles((files) => [...files, ...acceptedFiles]);
  };
  const proceedImages = async () => {
    // We’ll keep it simple: AI off => default title; AI on => still default (stub).
    const next = uploadedFiles.map((file, idx) => ({
      title: aiDetectionEnabled ? "Suggested task from image" : "Image Task",
      assignee: "Sarah Chen",
      file,
    }));
    setImageTasks(next);
    setUploadedFiles([]);
  };
  const confirmImageTasks = () => {
    const baseOrder = tasks.length;
    const toAdd = imageTasks.map((item, idx) => ({
      id: String(Date.now() + idx),
      title: item.title || "Image Task",
      description: "",
      status: "backlog",
      assignee: { name: item.assignee, avatar: avatar1 },
      reporter: { name: "Auto Import", avatar: avatar2 },
      deadline: new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10),
      screenshots: [],
      project: "website",
      country: "lebanon",
      group: "Imported",
      order: baseOrder + idx,
    }));
    setTasks((prev) => [...prev, ...toAdd]);
    setImageTasks([]);
    setImageModalOpen(false);
  };

  const availableAssignees = [
    { name: "Sarah Chen", avatar: avatar1 },
    { name: "Marcus Williams", avatar: avatar2 },
    { name: "Elena Rodriguez", avatar: avatar3 },
    { name: "David Park", avatar: avatar4 },
    { name: "Jessica Martinez", avatar: avatar5 },
  ];

  // --- Render helpers ---
  const TaskBadges = ({ task }) => {
    const daysLeft = getDaysLeft(task.id);
    return (
      <InlineStack gap="300" align="center">
        {task.attachments ? (
          <Badge tone="info">
            {task.attachments} file{task.attachments > 1 ? "s" : ""}
          </Badge>
        ) : null}
        <Badge
          tone={
            daysLeft < 0 ? "critical" : daysLeft < 10 ? "attention" : "subdued"
          }
        >
          {daysLeft}d
        </Badge>
        {isHighPriority(task.id) ? <Badge tone="critical">High</Badge> : null}
      </InlineStack>
    );
  };

  const People = ({ task }) => (
    <InlineStack gap="300" align="center">
      <InlineStack gap="200" align="center">
        <Avatar
          customer
          size="small"
          source={task.assignee.avatar}
          initials={initials(task.assignee.name)}
        />
        <Text as="span" variant="bodySm">
          {task.assignee.name}
        </Text>
      </InlineStack>
      <Divider direction="vertical" />
      <InlineStack gap="200" align="center">
        <Avatar
          size="small"
          source={task.reporter.avatar}
          initials={initials(task.reporter.name)}
        />
        <Text as="span" variant="bodySm">
          {task.reporter.name}
        </Text>
      </InlineStack>
    </InlineStack>
  );

  const Actions = ({ task }) => (
    <InlineStack gap="200">
      <Tooltip content="Mark as done">
        <Button
          icon={<Icon source={CheckCircleIcon} />}
          onClick={() => moveTask(task.id, "done")}
        />
      </Tooltip>
      <Tooltip content="Delete">
        <Button
          tone="critical"
          icon={<Icon source={DeleteIcon} />}
          onClick={() =>
            setTasks((prev) => prev.filter((t) => t.id !== task.id))
          }
        />
      </Tooltip>
      <Tooltip content="Move">
        <Select
          labelHidden
          label="Move status"
          options={statusOrder.map((s) => ({ label: s.label, value: s.key }))}
          value={task.status}
          onChange={(v) => moveTask(task.id, v)}
        />
      </Tooltip>
    </InlineStack>
  );

  const initials = (name) =>
    (name || "")
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();

  const TaskRow = ({ task }) => (
    <Box
      padding="300"
      borderBlockEndWidth="025"
      borderColor="border"
      background={
        isHighPriority(task.id) ? "bg-surface-critical-subdued" : "bg-surface"
      }
    >
      <InlineStack align="space-between" blockAlign="center">
        <InlineStack gap="300" align="center">
          <Button variant="plain" onClick={() => onTaskClick(task)}>
            <Text as="h3" variant="bodyMd" fontWeight="semibold">
              {task.title}
            </Text>
          </Button>
          <Tooltip content={task.description || "No description"}>
            <Icon source={ViewIcon} tone="subdued" />
          </Tooltip>
        </InlineStack>

        <InlineStack gap="400" align="center">
          {/* Screenshots preview (first 2) */}
          {task.screenshots && task.screenshots.length > 0 ? (
            <InlineStack gap="200">
              {task.screenshots.slice(0, 2).map((src, i) => (
                <Thumbnail key={i} source={src} size="small" alt="Screenshot" />
              ))}
              {task.screenshots.length > 2 ? (
                <Badge tone="subdued">+{task.screenshots.length - 2}</Badge>
              ) : null}
            </InlineStack>
          ) : null}

          <Divider direction="vertical" />

          <People task={task} />

          <Divider direction="vertical" />

          <InlineStack gap="200" align="center">
            <Icon source={CalendarIcon} />
            <Text as="span" variant="bodySm">
              {task.deadline}
            </Text>
            <Badge>{getRelativeDeadline(task.deadline)}</Badge>
          </InlineStack>

          <Divider direction="vertical" />

          <TaskBadges task={task} />

          <Divider direction="vertical" />

          <Actions task={task} />
        </InlineStack>
      </InlineStack>
    </Box>
  );

  const renderListTab = (status) => {
    const grouped = getGroupedTasksByStatus(status);
    const groups = Object.keys(grouped);
    if (groups.length === 0) {
      return (
        <Card>
          <Box padding="400">
            <Text as="p" tone="subdued">
              No tasks in this status.
            </Text>
          </Box>
        </Card>
      );
    }
    return (
      <BlockStack gap="500">
        {groups.map((group) => (
          <Card key={group} roundedAbove="sm">
            {/* <Card.Header title={group} /> */}
            <Text>{group} </Text>
            <Divider />
            <BlockStack>
              {grouped[group].map((task) => (
                <TaskRow key={task.id} task={task} />
              ))}
            </BlockStack>
          </Card>
        ))}
      </BlockStack>
    );
  };

  const KanbanColumn = ({ statusKey, title }) => {
    const items = filteredTasks
      .filter((t) => t.status === statusKey)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    const density =
      kanbanDisplayMode === "super-compact"
        ? "tight"
        : kanbanDisplayMode === "relaxed"
        ? "loose"
        : "regular";

    return (
      <Card roundedAbove="sm">
        {/* <Card.Header title={`${title} (${items.length})`} /> */}
        <Text>{`${title} (${items.length})`}</Text>
        <Divider />
        <BlockStack gap="0">
          {items.length === 0 ? (
            <Box padding="400">
              <Text as="p" tone="subdued">
                No tasks.
              </Text>
            </Box>
          ) : (
            items.map((task) => (
              <Box
                key={task.id}
                padding={
                  density === "tight"
                    ? "200"
                    : density === "loose"
                    ? "400"
                    : "300"
                }
                background={
                  isHighPriority(task.id)
                    ? "bg-surface-critical-subdued"
                    : "bg-surface"
                }
                borderBlockEndWidth="025"
                borderColor="border"
              >
                <BlockStack gap="200">
                  <InlineStack align="space-between" blockAlign="center">
                    <Button variant="plain" onClick={() => onTaskClick(task)}>
                      <Text as="h3" variant="bodyMd" fontWeight="semibold">
                        {task.title}
                      </Text>
                    </Button>
                    <InlineStack gap="100">
                      <Button
                        onClick={() => moveUp(task)}
                        accessibilityLabel="Move up"
                      >
                        ↑
                      </Button>
                      <Button
                        onClick={() => moveDown(task)}
                        accessibilityLabel="Move down"
                      >
                        ↓
                      </Button>
                    </InlineStack>
                  </InlineStack>

                  <InlineStack gap="300" align="center">
                    <Badge tone="subdued" size="small">
                      {task.group}
                    </Badge>
                    <Divider direction="vertical" />
                    <InlineStack gap="200" align="center">
                      <Avatar
                        customer
                        size="small"
                        source={task.assignee.avatar}
                        initials={initials(task.assignee.name)}
                      />
                      <Text as="span" variant="bodySm">
                        {task.assignee.name}
                      </Text>
                    </InlineStack>
                  </InlineStack>

                  <InlineStack align="space-between">
                    <InlineStack gap="200" align="center">
                      <Icon source={CalendarIcon} />
                      <Text as="span" variant="bodySm">
                        {task.deadline}
                      </Text>
                      <Badge>{getRelativeDeadline(task.deadline)}</Badge>
                    </InlineStack>
                    <TaskBadges task={task} />
                  </InlineStack>

                  <InlineStack gap="200">
                    <Select
                      labelHidden
                      label="Move status"
                      options={statusOrder.map((s) => ({
                        label: s.label,
                        value: s.key,
                      }))}
                      value={task.status}
                      onChange={(v) => moveTask(task.id, v)}
                    />
                    <Button
                      icon={<Icon source={CheckCircleIcon} />}
                      onClick={() => moveTask(task.id, "done")}
                    >
                      Done
                    </Button>
                    <Button
                      tone="critical"
                      icon={<Icon source={DeleteIcon} />}
                      onClick={() =>
                        setTasks((prev) => prev.filter((t) => t.id !== task.id))
                      }
                    >
                      Delete
                    </Button>
                  </InlineStack>
                </BlockStack>
              </Box>
            ))
          )}
        </BlockStack>
      </Card>
    );
  };

  const TimelineView = () => {
    const items = [...filteredTasks].sort((a, b) =>
      (a.deadline || "").localeCompare(b.deadline || "")
    );
    if (!items.length) {
      return (
        <Card>
          <Box padding="400">
            <Text as="p" tone="subdued">
              No tasks to show in timeline.
            </Text>
          </Box>
        </Card>
      );
    }
    return (
      <BlockStack gap="400">
        {items.map((task) => (
          <Card key={task.id} roundedAbove="sm">
            <BlockStack gap="300" padding="400">
              <InlineStack align="space-between" blockAlign="center">
                <InlineStack gap="300" align="center">
                  <Button variant="plain" onClick={() => onTaskClick(task)}>
                    <Text as="h3" variant="bodyMd" fontWeight="semibold">
                      {task.title}
                    </Text>
                  </Button>
                  <Badge tone="subdued">{task.group}</Badge>
                </InlineStack>

                <InlineStack gap="200" align="center">
                  <Icon source={CalendarTimeIcon} />
                  <Text as="span" variant="bodySm">
                    {task.deadline}
                  </Text>
                  <Badge>{getRelativeDeadline(task.deadline)}</Badge>
                </InlineStack>
              </InlineStack>

              <Divider />

              <InlineStack align="space-between" blockAlign="center">
                <InlineStack gap="200" align="center">
                  <Avatar
                    customer
                    size="small"
                    source={task.assignee.avatar}
                    initials={initials(task.assignee.name)}
                  />
                  <Text as="span" variant="bodySm">
                    {task.assignee.name}
                  </Text>
                  <Divider direction="vertical" />
                  <Avatar
                    size="small"
                    source={task.reporter.avatar}
                    initials={initials(task.reporter.name)}
                  />
                  <Text as="span" variant="bodySm">
                    {task.reporter.name}
                  </Text>
                </InlineStack>
                <TaskBadges task={task} />
              </InlineStack>
            </BlockStack>
          </Card>
        ))}
      </BlockStack>
    );
  };

  // --- UI ---
  return (
    <Page
      fullWidth
      title="Task Manager"
      primaryAction={{
        content: "Add tasks",
        onAction: () => setBulkModalOpen(true),
      }}
      secondaryActions={[
        {
          content: "From images",
          icon: ImageIcon,
          onAction: () => setImageModalOpen(true),
        },
      ]}
    >
      <Card>
        <Box padding="400">
          <InlineStack align="space-between" blockAlign="center">
            {/* Left side */}
            {/* <InlineStack gap="300" blockAlign="center">
              <Box
                background="bg-surface-brand"
                padding="200"
                borderRadius="200"
              >
                <Icon source={ViewIcon} />
              </Box>
              <Text as="h2" variant="headingLg">
                Task Manager
              </Text>
            </InlineStack> */}

            {/* Right side */}
            <InlineStack gap="200" align="center">
              {/* Countries */}
              {/* <ButtonGroup>
                <Button
                  pressed={selectedCountries.includes("lebanon")}
                  onClick={() => toggleCountry("lebanon")}
                >
                  🇱🇧
                </Button>
                <Button
                  pressed={selectedCountries.includes("egypt")}
                  onClick={() => toggleCountry("egypt")}
                >
                  🇪🇬
                </Button>
                <Button
                  pressed={selectedCountries.includes("iraq")}
                  onClick={() => toggleCountry("iraq")}
                >
                  🇮🇶
                </Button>
              </ButtonGroup> */}

              {/* <Divider direction="vertical" /> */}

              {/* Projects */}
              <ButtonGroup>
                <Button
                  pressed={selectedProject === "all"}
                  onClick={() => setSelectedProject("all")}
                >
                  All
                </Button>
                <Button
                  pressed={selectedProject === "mobile"}
                  icon={MobileIcon}
                  onClick={() => setSelectedProject("mobile")}
                >
                  Mobile
                </Button>
                <Button
                  pressed={selectedProject === "website"}
                  icon={StoreIcon}
                  onClick={() => setSelectedProject("website")}
                >
                  Website
                </Button>
              </ButtonGroup>

              <Divider direction="vertical" />

              {/* View mode */}
              {/* <ButtonGroup>
                <Button
                  pressed={viewMode === "list"}
                  onClick={() => setViewMode("list")}
                >
                  List
                </Button>
                <Button
                  pressed={viewMode === "kanban"}
                  onClick={() => setViewMode("kanban")}
                >
                  Kanban
                </Button>
                <Button
                  pressed={viewMode === "timeline"}
                  onClick={() => setViewMode("timeline")}
                >
                  Timeline
                </Button>
              </ButtonGroup> */}

              {viewMode === "kanban" && (
                <>
                  <Divider direction="vertical" />
                  <Select
                    labelHidden
                    label="Kanban density"
                    value={kanbanDisplayMode}
                    onChange={setKanbanDisplayMode}
                    options={[
                      { label: "Super Compact", value: "super-compact" },
                      { label: "Compact", value: "compact" },
                      { label: "Relaxed", value: "relaxed" },
                    ]}
                  />
                </>
              )}
            </InlineStack>
          </InlineStack>
        </Box>
      </Card>

      <Box paddingBlockStart="400">
        {viewMode === "list" ? (
          <>
            <Tabs
              tabs={tabs}
              selected={selectedTab}
              onSelect={setSelectedTab}
            />
            <Box paddingBlockStart="400">{renderListTab(selectedStatus)}</Box>
          </>
        ) : viewMode === "kanban" ? (
          <BlockStack gap="400">
            <InlineStack align="space-between" wrap={true}>
              {statusOrder.map((s) => (
                <Box
                  key={s.key}
                  minInlineSize="320px"
                  maxInlineSize="640px"
                  width="100%"
                >
                  <KanbanColumn statusKey={s.key} title={s.label} />
                </Box>
              ))}
            </InlineStack>
          </BlockStack>
        ) : (
          <TimelineView />
        )}
      </Box>

      {/* Details Modal */}
      <Modal
        open={detailsModal.open}
        onClose={() => setDetailsModal({ open: false, task: null })}
        title={detailsModal.task?.title || "Task details"}
        primaryAction={{
          content: isEditingDesc ? "Save" : "Mark as done",
          onAction: () => {
            if (!detailsModal.task) return;
            if (isEditingDesc) {
              setTasks((prev) =>
                prev.map((t) =>
                  t.id === detailsModal.task.id
                    ? { ...t, description: editedDesc }
                    : t
                )
              );
              setIsEditingDesc(false);
            } else {
              moveTask(detailsModal.task.id, "done");
            }
          },
        }}
        secondaryActions={[
          {
            content: isEditingDesc ? "Cancel" : "Edit description",
            onAction: () => {
              if (!detailsModal.task) return;
              if (isEditingDesc) {
                setEditedDesc(detailsModal.task.description || "");
                setIsEditingDesc(false);
              } else {
                setIsEditingDesc(true);
              }
            },
          },
        ]}
      >
        <Modal.Section>
          {detailsModal.task ? (
            <BlockStack gap="400">
              <InlineStack gap="400" align="center">
                <Badge tone="subdued" size="small">
                  {detailsModal.task.project}
                </Badge>
                <Badge tone="subdued" size="small">
                  {countryFlag[detailsModal.task.country]}{" "}
                  {detailsModal.task.country}
                </Badge>
                <Badge size="small">{detailsModal.task.group}</Badge>
              </InlineStack>

              <Divider />

              <BlockStack gap="200">
                <Text as="h4" variant="headingSm">
                  Description
                </Text>
                {!isEditingDesc ? (
                  <Text as="p" tone="subdued">
                    {detailsModal.task.description || "No description."}
                  </Text>
                ) : (
                  <TextField
                    label="Description"
                    value={editedDesc}
                    onChange={setEditedDesc}
                    multiline={4}
                    autoComplete="off"
                  />
                )}
              </BlockStack>

              <Divider />

              <InlineStack align="space-between" blockAlign="center">
                <InlineStack gap="200" align="center">
                  <Icon source={CalendarIcon} />
                  <Text as="span" variant="bodySm">
                    {detailsModal.task.deadline}
                  </Text>
                  <Badge>
                    {getRelativeDeadline(detailsModal.task.deadline)}
                  </Badge>
                </InlineStack>
                <TaskBadges task={detailsModal.task} />
              </InlineStack>

              {detailsModal.task.screenshots?.length ? (
                <>
                  <Divider />
                  <BlockStack gap="200">
                    <Text as="h4" variant="headingSm">
                      Screenshots
                    </Text>
                    <InlineStack gap="200" wrap={true}>
                      {detailsModal.task.screenshots.map((src, i) => (
                        <Thumbnail
                          key={i}
                          source={src}
                          size="large"
                          alt={`screenshot-${i}`}
                        />
                      ))}
                    </InlineStack>
                  </BlockStack>
                </>
              ) : null}

              <Divider />

              <BlockStack gap="300">
                <Text as="h4" variant="headingSm">
                  Team
                </Text>
                <InlineStack gap="400" align="center">
                  <Avatar
                    customer
                    source={detailsModal.task.assignee.avatar}
                    initials={initials(detailsModal.task.assignee.name)}
                  />
                  <Text as="span" variant="bodySm">
                    {detailsModal.task.assignee.name} — Assignee
                  </Text>
                </InlineStack>
                <InlineStack gap="400" align="center">
                  <Avatar
                    source={detailsModal.task.reporter.avatar}
                    initials={initials(detailsModal.task.reporter.name)}
                  />
                  <Text as="span" variant="bodySm">
                    {detailsModal.task.reporter.name} — Reporter
                  </Text>
                </InlineStack>
              </BlockStack>
            </BlockStack>
          ) : null}
        </Modal.Section>
      </Modal>

      {/* Bulk Add Modal */}
      <Modal
        open={bulkModalOpen}
        onClose={() => setBulkModalOpen(false)}
        title="Add tasks in bulk"
        primaryAction={{
          content: parsedBulk.length
            ? `Add ${parsedBulk.length} task(s)`
            : "Parse",
          onAction: parsedBulk.length ? confirmBulkAdd : parseBulkNow,
          disabled: !bulkInput.trim() && !parsedBulk.length,
        }}
        secondaryActions={[
          parsedBulk.length
            ? {
                content: "Back",
                onAction: () => setParsedBulk([]),
              }
            : {
                content: "Cancel",
                onAction: () => setBulkModalOpen(false),
              },
        ]}
      >
        <Modal.Section>
          {!parsedBulk.length ? (
            <BlockStack gap="300">
              <TextField
                label="Tasks (one per line)"
                value={bulkInput}
                onChange={setBulkInput}
                multiline={10}
                autoComplete="off"
                placeholder={"Task 1\nTask 2\nTask 3"}
              />
              <Text tone="subdued" as="p">
                Paste or type each task on a new line. Press <b>Parse</b> to
                review before adding.
              </Text>
            </BlockStack>
          ) : (
            <BlockStack gap="300">
              <Banner>
                Assign people to each parsed task (default: Sarah Chen).
              </Banner>
              {parsedBulk.map((p, idx) => (
                <InlineStack key={idx} gap="300" align="center">
                  <Box minWidth="40%">
                    <Text as="p">{p.title}</Text>
                  </Box>
                  <Select
                    labelHidden
                    label="Assignee"
                    options={availableAssignees.map((a) => ({
                      label: a.name,
                      value: a.name,
                    }))}
                    value={p.assignee}
                    onChange={(v) => {
                      setParsedBulk((prev) => {
                        const copy = [...prev];
                        copy[idx] = { ...copy[idx], assignee: v };
                        return copy;
                      });
                    }}
                  />
                  <Button
                    tone="critical"
                    onClick={() =>
                      setParsedBulk((prev) => prev.filter((_, i) => i !== idx))
                    }
                  >
                    Remove
                  </Button>
                </InlineStack>
              ))}
            </BlockStack>
          )}
        </Modal.Section>
      </Modal>

      {/* Images to Tasks Modal */}
      <Modal
        open={imageModalOpen}
        onClose={() => {
          setImageModalOpen(false);
          setUploadedFiles([]);
          setImageTasks([]);
        }}
        title="Create tasks from images"
        primaryAction={{
          content: imageTasks.length
            ? `Add ${imageTasks.length} task(s)`
            : "Continue",
          onAction: imageTasks.length ? confirmImageTasks : proceedImages,
          disabled: !uploadedFiles.length && !imageTasks.length,
        }}
        secondaryActions={[
          imageTasks.length
            ? {
                content: "Back",
                onAction: () => {
                  setImageTasks([]);
                },
              }
            : {
                content: "Cancel",
                onAction: () => setImageModalOpen(false),
              },
        ]}
      >
        <Modal.Section>
          {!imageTasks.length ? (
            <BlockStack gap="400">
              <ChoiceList
                title="AI Task Detection"
                choices={[
                  { label: "Enabled (suggest titles)", value: "on" },
                  { label: "Disabled (use 'Image Task')", value: "off" },
                ]}
                selected={[aiDetectionEnabled ? "on" : "off"]}
                onChange={(v) => setAiDetectionEnabled(v.includes("on"))}
              />
              <DropZone
                onDrop={handleDropZoneDrop}
                accept="image/*"
                allowMultiple
              >
                <DropZone.FileUpload />
              </DropZone>

              {uploadedFiles.length ? (
                <BlockStack gap="200">
                  <Text as="p" variant="bodySm">
                    {uploadedFiles.length} image(s) selected
                  </Text>
                  <InlineStack gap="200" wrap={true}>
                    {uploadedFiles.map((file, i) => (
                      <Thumbnail
                        key={i}
                        size="large"
                        alt={file.name}
                        source={URL.createObjectURL(file)}
                      />
                    ))}
                  </InlineStack>
                </BlockStack>
              ) : null}
            </BlockStack>
          ) : (
            <BlockStack gap="300">
              {imageTasks.map((t, idx) => (
                <Card key={idx} roundedAbove="sm">
                  <Box padding="400">
                    <InlineStack gap="300" align="center" blockAlign="center">
                      <Thumbnail
                        size="large"
                        alt={t.file?.name || "image"}
                        source={t.file ? URL.createObjectURL(t.file) : ""}
                      />

                      <TextField
                        label="Task title"
                        labelHidden
                        value={t.title}
                        onChange={(v) => {
                          setImageTasks((prev) => {
                            const copy = [...prev];
                            copy[idx] = { ...copy[idx], title: v };
                            return copy;
                          });
                        }}
                      />

                      <Select
                        labelHidden
                        label="Assignee"
                        options={availableAssignees.map((a) => ({
                          label: a.name,
                          value: a.name,
                        }))}
                        value={t.assignee}
                        onChange={(v) => {
                          setImageTasks((prev) => {
                            const copy = [...prev];
                            copy[idx] = { ...copy[idx], assignee: v };
                            return copy;
                          });
                        }}
                      />

                      <Button
                        tone="critical"
                        onClick={() =>
                          setImageTasks((prev) =>
                            prev.filter((_, i) => i !== idx)
                          )
                        }
                      >
                        Remove
                      </Button>
                    </InlineStack>
                  </Box>
                </Card>
              ))}
            </BlockStack>
          )}
        </Modal.Section>
      </Modal>
    </Page>
  );
}

export default TaskManager;
